/**
 * Admin copy for D615 SETUP-READINESS: **Configurações › Configuração da plataforma** — the checklist of what is still missing for
 * the platform to work end to end. Only the screen's own sentences live here; the words for an item (`setup.item`, its about
 * and steps), a state, an area, a level and a reason come from the central vocabulary (`setup.*`), never from a code.
 *
 * Kept in its own file; `messages.ts` spreads it into the catalog and asserts that no id collides. Same four base languages,
 * one entry per id, so a missing translation is a compile error. Neutral: no country, currency or vendor literal (vendors are
 * named by the `{vendor}` variable).
 */
type SetupEntry = Readonly<{ pt: string; en: string; es: string; it: string }>;

export const PLATFORM_SETUP_DICT: Readonly<Record<string, SetupEntry>> = {
  'nav.platformSetup': { pt: 'Configuração da plataforma', en: 'Platform setup', es: 'Configuración de la plataforma', it: 'Configurazione della piattaforma' },
  'setup.title': { pt: 'Configuração da plataforma', en: 'Platform setup', es: 'Configuración de la plataforma', it: 'Configurazione della piattaforma' },
  'setup.subtitle': {
    pt: 'Lista o que ainda falta para a plataforma funcionar de ponta a ponta e leva você até onde cada item se resolve.',
    en: 'Lists what is still missing for the platform to work end to end and takes you to where each item is fixed.',
    es: 'Lista lo que aún falta para que la plataforma funcione de punta a punta y te lleva a donde se resuelve cada punto.',
    it: 'Elenca ciò che manca ancora perché la piattaforma funzioni end to end e ti porta dove si risolve ogni voce.',
  },
  'setup.progress.label': { pt: '{ready} de {required} itens prontos', en: '{ready} of {required} items ready', es: '{ready} de {required} elementos listos', it: '{ready} di {required} voci pronte' },
  'setup.summary.pending.one': {
    pt: 'Falta {count} item para a plataforma ficar 100% funcional.',
    en: '{count} item is missing for the platform to be 100% functional.',
    es: 'Falta {count} elemento para que la plataforma quede 100% funcional.',
    it: 'Manca {count} voce perché la piattaforma sia al 100% funzionante.',
  },
  'setup.summary.pending.other': {
    pt: 'Faltam {count} itens para a plataforma ficar 100% funcional.',
    en: '{count} items are missing for the platform to be 100% functional.',
    es: 'Faltan {count} elementos para que la plataforma quede 100% funcional.',
    it: 'Mancano {count} voci perché la piattaforma sia al 100% funzionante.',
  },
  'setup.summary.unverified.one': {
    pt: 'Nada pendente encontrado, mas {count} item não pôde ser verificado agora.',
    en: 'Nothing pending found, but {count} item could not be checked right now.',
    es: 'No se encontró nada pendiente, pero {count} elemento no pudo verificarse ahora.',
    it: 'Nessuna voce in sospeso trovata, ma {count} voce non è stata verificabile ora.',
  },
  'setup.summary.unverified.other': {
    pt: 'Nada pendente encontrado, mas {count} itens não puderam ser verificados agora.',
    en: 'Nothing pending found, but {count} items could not be checked right now.',
    es: 'No se encontró nada pendiente, pero {count} elementos no pudieron verificarse ahora.',
    it: 'Nessuna voce in sospeso trovata, ma {count} voci non sono state verificabili ora.',
  },
  'setup.place.unnamed': { pt: 'Cidade sem nome cadastrado', en: 'City without a registered name', es: 'Ciudad sin nombre registrado', it: 'Città senza nome registrato' },
  'setup.summary.done': {
    pt: 'Tudo pronto: a plataforma tem tudo o que precisa para funcionar.',
    en: 'All set: the platform has everything it needs to work.',
    es: 'Todo listo: la plataforma tiene todo lo que necesita para funcionar.',
    it: 'Tutto pronto: la piattaforma ha tutto ciò che serve per funzionare.',
  },
  'setup.summary.empty': {
    pt: 'Nenhum item desta lista é exigido de você neste escopo.',
    en: 'No item on this list is required of you in this scope.',
    es: 'Ningún punto de esta lista se te exige en este alcance.',
    it: 'Nessuna voce di questo elenco è richiesta a te in questo ambito.',
  },
  'setup.evaluatedAt': { pt: 'Verificado em {when}', en: 'Checked on {when}', es: 'Verificado el {when}', it: 'Verificato il {when}' },
  'setup.recheck': { pt: 'Verificar de novo', en: 'Check again', es: 'Verificar de nuevo', it: 'Verifica di nuovo' },
  'setup.updatedAt': { pt: 'Alterado em {when}', en: 'Changed on {when}', es: 'Cambiado el {when}', it: 'Modificato il {when}' },
  'setup.verifiedAt': { pt: 'Testado com sucesso em {when}', en: 'Successfully tested on {when}', es: 'Probado con éxito el {when}', it: 'Testato con successo il {when}' },
  'setup.keys.title': { pt: 'Regras que ainda faltam definir', en: 'Rules still to be set', es: 'Reglas que aún faltan por definir', it: 'Regole ancora da definire' },
  'setup.help.title': { pt: 'O que é e como resolver', en: 'What it is and how to fix it', es: 'Qué es y cómo resolverlo', it: 'Cos’è e come risolverlo' },
  'setup.help.steps': { pt: 'Passo a passo', en: 'Step by step', es: 'Paso a paso', it: 'Passo dopo passo' },
  'setup.open': { pt: 'Abrir a tela', en: 'Open the screen', es: 'Abrir la pantalla', it: 'Apri la schermata' },
  'setup.vendor': { pt: 'Abrir o painel do fornecedor ({vendor})', en: 'Open the vendor console ({vendor})', es: 'Abrir el panel del proveedor ({vendor})', it: 'Apri il pannello del fornitore ({vendor})' },
  'setup.done': { pt: 'Já configurado ({count})', en: 'Already set up ({count})', es: 'Ya configurado ({count})', it: 'Già configurato ({count})' },
  'setup.informative': {
    pt: 'Ainda não disponível ou sem leitura ({count})',
    en: 'Not available yet or not readable ({count})',
    es: 'Aún no disponible o sin lectura ({count})',
    it: 'Non ancora disponibile o non leggibile ({count})',
  },
  'setup.unavailable.title': { pt: 'Não verificados', en: 'Not checked', es: 'Sin verificar', it: 'Non verificati' },
  'setup.unavailable.note': { pt: 'Não foi possível verificar agora', en: 'Could not be checked right now', es: 'No se pudo verificar ahora', it: 'Non è stato possibile verificare ora' },
  'setup.error': {
    pt: 'Não foi possível carregar a configuração da plataforma.',
    en: 'Could not load the platform setup.',
    es: 'No se pudo cargar la configuración de la plataforma.',
    it: 'Impossibile caricare la configurazione della piattaforma.',
  },
  'setup.noItems': {
    pt: 'Nenhum item pôde ser lido com as suas permissões neste escopo.',
    en: 'No item could be read with your permissions in this scope.',
    es: 'Ningún punto pudo leerse con tus permisos en este alcance.',
    it: 'Nessuna voce è leggibile con i tuoi permessi in questo ambito.',
  },
  'setup.alert.pending.one': {
    pt: 'Configuração da plataforma: falta {count} item',
    en: 'Platform setup: {count} item missing',
    es: 'Configuración de la plataforma: falta {count} elemento',
    it: 'Configurazione della piattaforma: manca {count} voce',
  },
  'setup.alert.pending.other': {
    pt: 'Configuração da plataforma: faltam {count} itens',
    en: 'Platform setup: {count} items missing',
    es: 'Configuración de la plataforma: faltan {count} elementos',
    it: 'Configurazione della piattaforma: mancano {count} voci',
  },
};
